import * as React from 'react';

import styled, { ThemeContext } from 'styled-components';

import { COLOR, FONT_SIZE, FONT_STACK, SPACING, SPEED, Theme } from '../../packages/constants/';
import { LoadingSpinner } from '../loading-spinner';

export interface ButtonProps {
    /**
     * Button content
     */
    children?: React.ReactNode;
    /**
     * Data Test ID
     */
    dataId: string;
    /**
     * Optional click handler
     */
    onClick?: (e: any) => void;
    /**
     * Is it disabled?
     */
    disabled?: boolean;
    /**
     * Value
     */
    value?: string;
    /**
     * Is it loading?
     */
    loading?: boolean;
    /**
     * Is it inline?
     */
    inline?: boolean;
    /**
     * Theme
     */
    theme?: Theme;
    themeProvider?: {};
    /**
     * Minimum width
     */
    minWidth?: string;
    /**
     * Color
     */
    color?: string;
    /**
     * size
     */
    size?: string;
    /**
     * Button type
     */
    type?: 'button' | 'submit' | 'reset' | undefined;
    className?: string;
    /** Allows the button to be assosciated with a form it is not a direct child of */
    form?: string;
    variant?: 'medium' | 'large';
}
export interface ButtonAsTextProps {
    children: React.ReactNode;
    /**
     * Data Test ID cat
     */
    dataId: string;
    /**
     * Optional click handler
     */
    onClick?: (e: any) => void;
    /**
     * Is it disabled?
     */
    disabled?: boolean;
    /**
     * Value
     */
    value?: string;
    /**
     * Is it inline?
     */
    inline?: boolean;
    /**
     * Theme
     */
    theme?: {
        main: string;
        mainText: string;
        mainBorder: string;
        spinnerColor: 'black' | 'white';
        hover?: { main?: string; mainBorder?: string; mainText?: string };
    };
    themeProvider?: {};
    /**
     * Minimum width
     */
    minWidth?: string;
    /**
     * size
     */
    size?: string;
    /**
     * Button type
     */
    type?: 'button' | 'submit' | 'reset' | undefined;
    className?: string;
}
export interface ButtonGroupProps {
    /**
     * ButtonGroup content
     */
    children: React.ReactNode;
    /**
     * ButtonGroup alignment
     */
    justify?: 'left' | 'center' | 'right' | 'space-between' | undefined;
}

const StyledButton = styled.button.attrs<ButtonProps>(({ dataId }) => ({
    'data-test-id': dataId,
}))<ButtonProps>`
    display: ${(props) => (props.inline ? 'inline-flex' : 'flex')};
    justify-content: center;
    align-items: center;
    border-radius: ${(props) => (props.variant === 'large' ? '5px' : '2px')};
    border-width: 2px;
    border-style: solid;
    border-color: ${(props) => (props.theme?.mainBorder ? props.theme.mainBorder : '')};
    background-color: ${(props) => (props.theme ? props.theme.main : COLOR.BRAND.REPORTING)};
    color: ${(props) => (props.theme ? props.theme.mainText : COLOR.PRIMARY.WHITE)};
    cursor: pointer;
    font-size: ${(props) =>
        props.size ? props.size : props.variant === 'large' ? FONT_SIZE.SMALL : FONT_SIZE.X_SMALL};
    font-weight: 700;
    line-height: 20px;
    text-decoration: none;
    padding: ${(props) => (props.variant === 'large' ? '10px 20px' : SPACING.BUTTON_DEFAULT)};
    min-width: ${(props) => props.minWidth || '0'};

    :disabled {
        opacity: 0.3;
        cursor: default;
    }

    :hover:not(:disabled) {
        border-color: ${(props) => (props.theme?.hover?.mainBorder ? props.theme.hover.mainBorder : '')};
        background-color: ${({ theme }) => (theme && theme.hover ? theme.hover.main : COLOR.BRAND.REPORTING)};
        color: ${({ theme }) => (theme && theme.hover ? theme.hover.mainText : COLOR.PRIMARY.WHITE)};
    }
`;

/**
 * Primary UI component for user interaction
 *
 * - Use the primary button to help users know what to access next.
 * - ~~Use a secondary button where the action is not compulsory and there is already a primary button.~~ (We have
 * yet to decide on secondary button styles)
 * - Use a generic button for cancelling, or configuration.
 * - Don't use the primary button in conjunction with another primary button.
 **/

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            onClick,
            disabled = false,
            inline,
            color,
            size,
            type = 'button',
            loading,
            variant = 'medium',
            ...props
        },
        ref,
    ) => {
        const themeContext = React.useContext(ThemeContext);
        return (
            <StyledButton
                onClick={onClick}
                disabled={disabled}
                inline={inline}
                color={color}
                size={size}
                type={type}
                ref={ref}
                variant={variant}
                {...props}
            >
                {loading ? (
                    <LoadingSpinner color={themeContext ? themeContext.spinnerColor : 'white'}>
                        {children}
                    </LoadingSpinner>
                ) : (
                    children
                )}
            </StyledButton>
        );
    },
);

Button.displayName = 'Button';

const StyledTextButton = styled.button.attrs<ButtonProps>(({ dataId }) => ({
    'data-test-id': dataId,
}))`
    background-color: transparent;
    display: ${(props: ButtonProps) => (props.inline ? 'inline-flex' : 'flex')};
    justify-content: flex-start;
    align-items: center;
    color: ${(props) =>
        props.theme
            ? props.theme.mainBorder
                ? props.theme.mainBorder
                : COLOR.BRAND.REPORTING
            : COLOR.BRAND.REPORTING};
    cursor: pointer;
    font-family: ${FONT_STACK};
    font-size: ${(props: ButtonProps) => props.size || FONT_SIZE.X_SMALL};
    font-weight: 700;
    line-height: ${FONT_SIZE.SMALL};
    text-decoration: none;
    padding: 0 0 1px;
    margin-right: ${SPACING.X_LARGE};
    border: 0;
    border-bottom: 1px solid transparent;
    transition: border-bottom-color ${SPEED.TRANSITION};

    &:disabled {
        opacity: 0.3;
        cursor: default;
    }

    &:last-of-type {
        margin-right: 0;
    }

    &:hover:not(:disabled) {
        border-bottom-width: 1px;
        border-bottom-style: solid;
        border-bottom-color: ${(props: ButtonProps) =>
            props.theme && props.theme.hover ? props.theme.hover.main : COLOR.BRAND.REPORTING};
    }
`;

/**
 * Text UI component for user interaction
 */
const ButtonAsText = React.forwardRef<HTMLButtonElement, ButtonAsTextProps>(
    ({ children, onClick, disabled, inline, size, type, ...props }, ref) => (
        <StyledTextButton
            onClick={onClick}
            disabled={disabled}
            inline={inline}
            size={size}
            type={type}
            ref={ref}
            {...props}
        >
            {children}
        </StyledTextButton>
    ),
);
ButtonAsText.displayName = 'ButtonAsText';

const StyledButtonGroup = styled.div<ButtonGroupProps>`
    display: flex;
    align-items: center;
    justify-content: ${({ justify }) =>
        justify === 'left'
            ? 'flex-start'
            : justify === 'center'
            ? 'center'
            : justify === 'right'
            ? 'flex-end'
            : justify === 'space-between'
            ? 'space-between'
            : 'center'};

    ${StyledButton} {
        margin-right: ${SPACING.XX_LARGE};

        &:last-child {
            margin-right: 0;
        }
    }
`;

const ButtonGroup = ({ children, justify, ...props }: ButtonGroupProps) => (
    <StyledButtonGroup justify={justify} {...props}>
        {children}
    </StyledButtonGroup>
);

export { Button, ButtonAsText, ButtonGroup };
